/**
 * Draggable Class Block Component
 *
 * Represents a scheduled class in the grid with drag-and-drop functionality.
 * Uses @dnd-kit/core for draggable behavior.
 */

import React from 'react';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import ScheduleStatusIcon from '../tabs/ScheduleGridTab/components/ScheduleStatusIcon';
import type { ScheduledClass } from '../../../types/scheduling';

interface DraggableClassBlockProps {
  scheduledClass: ScheduledClass;
  primaryColor: string;
  blockColor?: string; // Optional color for the block (defaults to primaryColor)
  fontFamily: string | undefined;
  topPercent: number;
  heightPx: number;
  leftPercent?: number;
  widthPercent?: number;
  isEditable: boolean;
  onClick: (event: React.MouseEvent, scheduledClass: ScheduledClass) => void;
}

/**
 * Generate comprehensive tooltip content for a scheduled class
 */
const generateTooltipContent = (
  scheduledClass: ScheduledClass
): React.ReactNode => {
  // Rename snake_case to camelCase for ESLint
  const classDetails = scheduledClass.class_details;
  const roomDetails = scheduledClass.room_details;
  const teacherDetails = scheduledClass.teacher_details;
  const { day } = scheduledClass;
  const startTime = scheduledClass.start_time;
  const endTime = scheduledClass.end_time;

  // Format time to 12-hour clock with AM/PM
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.substring(0, 5).split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12; // Convert 0 to 12 for midnight
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Format styles - access from classDetails.styles array
  const styles =
    classDetails.styles && classDetails.styles.length > 0
      ? classDetails.styles.map((s: { title: string }) => s.title).join(', ')
      : 'No styles';

  // Format teachers
  const teachers =
    teacherDetails && teacherDetails.length > 0
      ? teacherDetails.map(t => t.teacher_name).join(', ')
      : 'No teacher assigned';

  // Calculate duration in minutes from classDetails.duration (in hours)
  const durationMinutes = Math.round(classDetails.duration * 60);

  return (
    <Box sx={{ p: 1 }}>
      {/* Class Name */}
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
        {classDetails.class_name}
      </Typography>

      {/* Styles */}
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Style(s):</strong> {styles}
      </Typography>

      {/* Level */}
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Level:</strong> {classDetails.level}
      </Typography>

      {/* Age Range */}
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Age Range:</strong> {classDetails.age_start}-
        {classDetails.age_end}
      </Typography>

      {/* Room */}
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Room:</strong> {roomDetails.room_name} (Capacity:{' '}
        {roomDetails.capacity})
      </Typography>

      {/* Teacher(s) */}
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Teacher(s):</strong> {teachers}
      </Typography>

      {/* Day & Time */}
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Schedule:</strong> {day} {formatTime(startTime)} -{' '}
        {formatTime(endTime)}
      </Typography>

      {/* Duration */}
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Duration:</strong> {durationMinutes} minutes (
        {classDetails.duration_slots} slots)
      </Typography>

      {/* Notes (if available) */}
      {classDetails.notes && (
        <Typography variant="body2" sx={{ mb: 0.5, fontStyle: 'italic' }}>
          <strong>Notes:</strong> {classDetails.notes}
        </Typography>
      )}

      {/* Scheduling Status */}
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 1, opacity: 0.8 }}
      >
        {scheduledClass.is_manually_scheduled
          ? '📌 Manually scheduled'
          : '🤖 Algorithm-generated'}
      </Typography>
    </Box>
  );
};

const DraggableClassBlock: React.FC<DraggableClassBlockProps> = ({
  scheduledClass,
  primaryColor,
  blockColor,
  fontFamily,
  topPercent,
  heightPx,
  leftPercent,
  widthPercent,
  isEditable,
  onClick,
}) => {
  // Use blockColor if provided, otherwise fall back to primaryColor
  const displayColor = blockColor || primaryColor;
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: scheduledClass.id.toString(),
      disabled: !isEditable,
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
      }
    : undefined;

  return (
    <Tooltip
      title={generateTooltipContent(scheduledClass)}
      arrow
      placement="right"
      enterDelay={500}
      enterNextDelay={500}
    >
      <Paper
        ref={setNodeRef}
        elevation={2}
        onClick={event => onClick(event, scheduledClass)}
        sx={{
          position: 'absolute',
          top: `${topPercent}%`,
          left: leftPercent !== undefined ? `${leftPercent}%` : '4px',
          width:
            widthPercent !== undefined
              ? `calc(${widthPercent}% - 8px)`
              : 'calc(100% - 8px)',
          height: `${heightPx}px`,
          bgcolor: displayColor,
          color: '#ffffff',
          cursor: isEditable ? 'pointer' : 'default',
          p: 0.5,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          zIndex: isDragging ? 1000 : 5,
          pointerEvents: 'auto', // Re-enable pointer events for class blocks
          '&:hover': isEditable
            ? {
                bgcolor: `${displayColor}dd`,
                zIndex: 10,
              }
            : {},
          ...style,
        }}
      >
        {/* Drag Handle (only in editable mode) */}
        {isEditable && (
          <Box
            /* eslint-disable react/jsx-props-no-spreading */
            {...listeners}
            {...attributes}
            /* eslint-enable react/jsx-props-no-spreading */
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '20px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab',
              borderRight: '1px solid rgba(255, 255, 255, 0.3)',
              '&:active': {
                cursor: 'grabbing',
              },
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: '12px', lineHeight: 1 }}
            >
              ⋮
            </Typography>
          </Box>
        )}
        {/* Manual/Robot Status Icon (only in editable mode) */}
        {isEditable && (
          <ScheduleStatusIcon
            isManuallyScheduled={scheduledClass.is_manually_scheduled || false}
          />
        )}
        {/* 1. Class name with level */}
        <Typography
          variant="caption"
          fontWeight="bold"
          sx={{
            fontSize: '0.75rem',
            lineHeight: 1.2,
            fontFamily,
            ml: isEditable ? '22px' : 0, // Add margin when drag handle is present
          }}
        >
          {scheduledClass.class_details.class_name} (
          {scheduledClass.class_details.level})
        </Typography>
        {/* 2. Age range */}
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            lineHeight: 1.1,
            fontFamily,
            ml: isEditable ? '22px' : 0,
          }}
        >
          Ages {scheduledClass.class_details.age_start}-
          {scheduledClass.class_details.age_end}
        </Typography>
        {/* 3. Teacher name */}
        {scheduledClass.teacher_details &&
          scheduledClass.teacher_details.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                lineHeight: 1.1,
                fontFamily,
                ml: isEditable ? '22px' : 0,
              }}
            >
              {scheduledClass.teacher_details
                .map(t => t.teacher_name)
                .join(', ')}
            </Typography>
          )}
        {/* 4. Room name */}
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            lineHeight: 1.1,
            fontFamily,
            ml: isEditable ? '22px' : 0,
          }}
        >
          {scheduledClass.room_details.room_name}
        </Typography>
      </Paper>
    </Tooltip>
  );
};

export default DraggableClassBlock;
